import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useAuth } from "../context/AuthContext";
import { updateQuote, regenerateQuoteText } from "../api/quotes";
import QuoteDetailsPage from "./QuoteDetails/QuoteDetailsPage";
import { formatCurrency } from "../utils/currency";
import {
  recalculateEstimate,
  normalizeEstimateForEditor,
} from "../pricing/recalculateEstimate";
import { round2 } from "../pricing/calculateEstimateTotals";
import {
  SOURCE_TYPE_LABELS,
  getRowQuantity,
  getRowUnitPrice,
  isManualRow,
  isAiAdjusted,
  isReviewRow,
  rowSignature,
} from "../pricing/estimateRows";
import {
  X,
  Download,
  Package2,
  ChevronDown,
  ChevronRight,
} from "lucide-react";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5050";



function safeObject(value, fallback = {}) {
  if (!value) return fallback;

  if (typeof value === "object" && !Array.isArray(value)) {
    return value;
  }

  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return typeof parsed === "object" &&
        parsed !== null &&
        !Array.isArray(parsed)
        ? parsed
        : fallback;
    } catch {
      return fallback;
    }
  }

  return fallback;
}

function safeArray(value) {
  if (Array.isArray(value)) return value;

  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  return [];
}

async function calculateQuoteFromBackend(parsedData, requestText = "") {
  const response = await fetch(`${API_BASE_URL}/api/calculate-quote`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      parsedData,
      requestText,
    }),
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => "");
    throw new Error(errorText || "Failed to calculate quote");
  }

  return response.json();
}

function mergeRowsPreserveManual(existingRows = [], rebuiltRows = []) {
  const manualMap = new Map();

  existingRows.forEach((row) => {
    if (isManualRow(row)) {
      manualMap.set(rowSignature(row), row);
    }
  });

  return rebuiltRows.map((row, index) => {
    const sig = rowSignature(row);

    if (manualMap.has(sig)) {
      const preserved = manualMap.get(sig);
      return {
        ...row,
        ...preserved,
        flags: {
          ...(row.flags || {}),
          ...(preserved.flags || {}),
        },
        meta: {
          ...(row.meta || {}),
          ...(preserved.meta || {}),
        },
      };
    }

    const oldRow = existingRows[index];
    if (isManualRow(oldRow) && rowSignature(oldRow) === sig) {
      return {
        ...row,
        ...oldRow,
        flags: {
          ...(row.flags || {}),
          ...(oldRow.flags || {}),
        },
        meta: {
          ...(row.meta || {}),
          ...(oldRow.meta || {}),
        },
      };
    }

    return row;
  });
}

function mergeEstimatePreserveManual(existingEstimate, rebuiltEstimate) {
  const existing = recalculateEstimate(
    normalizeEstimateForEditor(existingEstimate || {})
  );
  const rebuilt = recalculateEstimate(
    normalizeEstimateForEditor(rebuiltEstimate || {})
  );

  return recalculateEstimate({
    ...rebuilt,
    meta: {
      ...(rebuilt.meta || {}),
      marginPercent: Number(
        existing?.meta?.marginPercent ?? rebuilt?.meta?.marginPercent ?? 25
      ),
    },
    materials: mergeRowsPreserveManual(
      existing.materials || [],
      rebuilt.materials || []
    ),
    labour: mergeRowsPreserveManual(existing.labour || [], rebuilt.labour || []),
    extras: mergeRowsPreserveManual(existing.extras || [], rebuilt.extras || []),
  });
}

function formatDateTime(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("en-DK", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return value;
  }
}

function getActiveProjectTypes(parsedData = {}) {
  const primary =
    parsedData.primaryProjectType || parsedData.projectType || "custom";

  const projectTypes = Array.isArray(parsedData.projectTypes)
    ? parsedData.projectTypes.filter(Boolean)
    : [];

  return Array.from(new Set([primary, ...projectTypes]));
}

function validateParsedData(parsedData) {
  const errors = {};
  const projectTypes = getActiveProjectTypes(parsedData);

  if (!parsedData.projectName?.trim()) {
    errors.projectName = "Project name is required";
  }

  if (projectTypes.includes("flooring")) {
    const rooms = Array.isArray(parsedData.rooms) ? parsedData.rooms : [];

    if (rooms.length > 0) {
      rooms.forEach((item, index) => {
        if (Number(item.floorAreaM2 || 0) <= 0) {
          errors[`rooms.${index}`] = "Floor area must be greater than 0";
        }
      });
    } else if (Number(parsedData.floorAreaM2 || 0) <= 0) {
      errors.floorAreaM2 = "Floor area must be greater than 0";
    }
  }

  if (
    projectTypes.includes("wall") ||
    projectTypes.includes("internal_wall") ||
    projectTypes.includes("external_loadbearing_wall") ||
    projectTypes.includes("carport_garage")
  ) {
    const walls = Array.isArray(parsedData.walls) ? parsedData.walls : [];

    if (walls.length > 0) {
      walls.forEach((item, index) => {
        if (Number(item.lengthM || 0) <= 0 || Number(item.heightM || 0) <= 0) {
          errors[`walls.${index}`] =
            "Wall length and height must be greater than 0";
        }
      });
    } else {
      if (Number(parsedData.wallLengthM || 0) <= 0) {
        errors.wallLengthM = "Wall length must be greater than 0";
      }

      if (Number(parsedData.wallHeightM || 0) <= 0) {
        errors.wallHeightM = "Wall height must be greater than 0";
      }
    }
  }

  if (projectTypes.includes("terrace")) {
    const terraces = Array.isArray(parsedData.terraces) ? parsedData.terraces : [];

    if (terraces.length > 0) {
      terraces.forEach((item, index) => {
        const hasArea = Number(item.areaM2 || 0) > 0;
        const hasDimensions =
          Number(item.lengthM || 0) > 0 && Number(item.widthM || 0) > 0;

        if (!hasArea && !hasDimensions) {
          errors[`terraces.${index}`] =
            "Terrace dimensions or area are required";
        }
      });
    } else {
      const hasArea = Number(parsedData.terraceAreaM2 || 0) > 0;
      const hasDimensions =
        Number(parsedData.terraceLengthM || 0) > 0 &&
        Number(parsedData.terraceWidthM || 0) > 0;

      if (!hasArea && !hasDimensions) {
        errors.terraceAreaM2 = "Terrace dimensions or area are required";
      }
    }
  }

  if (projectTypes.includes("ceiling")) {
    const ceilings = Array.isArray(parsedData.ceilings) ? parsedData.ceilings : [];

    if (ceilings.length > 0) {
      ceilings.forEach((item, index) => {
        if (Number(item.ceilingAreaM2 || 0) <= 0) {
          errors[`ceilings.${index}`] = "Ceiling area must be greater than 0";
        }
      });
    } else if (Number(parsedData.ceilingAreaM2 || 0) <= 0) {
      errors.ceilingAreaM2 = "Ceiling area must be greater than 0";
    }
  }

  if (projectTypes.includes("painting")) {
    const sections = Array.isArray(parsedData.paintingSections)
      ? parsedData.paintingSections
      : [];

    if (sections.length > 0) {
      sections.forEach((item, index) => {
        if (Number(item.areaM2 || 0) <= 0) {
          errors[`paintingSections.${index}`] =
            "Painting area must be greater than 0";
        }
      });
    } else if (Number(parsedData.paintingAreaM2 || 0) <= 0) {
      errors.paintingAreaM2 = "Painting area must be greater than 0";
    }
  }

  if (projectTypes.includes("windows_doors")) {
    const windows = Array.isArray(parsedData.windows) ? parsedData.windows : [];
    const doors = Array.isArray(parsedData.doors) ? parsedData.doors : [];

    windows.forEach((item, index) => {
      if (Number(item.widthM || 0) <= 0 || Number(item.heightM || 0) <= 0) {
        errors[`windows.${index}`] =
          "Window width and height must be greater than 0";
      }
    });

    doors.forEach((item, index) => {
      if (Number(item.widthM || 0) <= 0 || Number(item.heightM || 0) <= 0) {
        errors[`doors.${index}`] =
          "Door width and height must be greater than 0";
      }
    });

    if (windows.length === 0 && doors.length === 0) {
      errors.windows = "Add at least one window or door";
    }
  }

  if (projectTypes.includes("roof")) {
    const roofs = Array.isArray(parsedData.roofs) ? parsedData.roofs : [];

    roofs.forEach((item, index) => {
      if (Number(item.areaM2 || 0) <= 0) {
        errors[`roofs.${index}`] = "Roof area must be greater than 0";
      }
    });

    if (roofs.length === 0 && Number(parsedData.roofAreaM2 || 0) <= 0) {
      errors.roofs = "Add at least one roof section or set roof area";
    }
  }

  return errors;
}

function getRowDisplayLabel(row) {
  return row?.label || row?.name || "Untitled row";
}

function getCustomerName(customer = {}) {
  return (
    customer?.name ||
    customer?.full_name ||
    customer?.company_name ||
    customer?.email ||
    ""
  );
}

function getRowSourceType(row) {
  return row?.meta?.sourceType || "unknown";
}

function getRowSourceId(row) {
  return row?.meta?.sourceId || "unknown";
}

function getRowSourceLabel(row) {
  return (
    row?.meta?.sourceLabel ||
    SOURCE_TYPE_LABELS[getRowSourceType(row)] ||
    SOURCE_TYPE_LABELS.unknown
  );
}

function groupEstimateBySection(estimate) {
  const normalized = recalculateEstimate(normalizeEstimateForEditor(estimate || {}));
  const grouped = new Map();

  const ensure = (row) => {
    const sourceType = getRowSourceType(row);
    const sourceId = getRowSourceId(row);
    const sourceLabel = getRowSourceLabel(row);
    const key = `${sourceType}__${sourceId}`;

    if (!grouped.has(key)) {
      grouped.set(key, {
        key,
        sourceType,
        sourceId,
        sourceLabel,
        sourceTypeLabel:
          SOURCE_TYPE_LABELS[sourceType] || SOURCE_TYPE_LABELS.unknown,
        materials: [],
        labour: [],
        extras: [],
      });
    }

    return grouped.get(key);
  };

  (normalized?.materials || []).forEach((row) => {
    ensure(row).materials.push(row);
  });

  (normalized?.labour || []).forEach((row) => {
    ensure(row).labour.push(row);
  });

  (normalized?.extras || []).forEach((row) => {
    ensure(row).extras.push(row);
  });

  return Array.from(grouped.values())
    .map((group) => {
      const materialsTotal = round2(
        group.materials.reduce((sum, row) => sum + Number(row.total || 0), 0)
      );
      const labourTotal = round2(
        group.labour.reduce((sum, row) => sum + Number(row.total || 0), 0)
      );
      const extrasTotal = round2(
        group.extras.reduce((sum, row) => sum + Number(row.total || 0), 0)
      );

      const allRows = [...group.materials, ...group.labour, ...group.extras];

      return {
        ...group,
        materialsTotal,
        labourTotal,
        extrasTotal,
        total: round2(materialsTotal + labourTotal + extrasTotal),
        rowsCount: allRows.length,
        hasManual: allRows.some((row) => isManualRow(row)),
        hasAiAdjusted: allRows.some((row) => isAiAdjusted(row)),
        reviewCount: allRows.filter((row) => isReviewRow(row)).length,
      };
    })
    .sort((a, b) => b.total - a.total);
}

function formatBoomMeta(row) {
  const meta = row?.meta || {};
  const parts = [];

  if (meta.widthM && meta.heightM) {
    parts.push(`${meta.widthM} × ${meta.heightM} m`);
  }

  if (meta.areaM2) {
    parts.push(`${meta.areaM2} m²`);
  }

  if (meta.netArea) {
    parts.push(`net ${meta.netArea} m²`);
  }

  if (meta.flooringType) {
    parts.push(String(meta.flooringType));
  }

  if (meta.frameMaterial) {
    parts.push(`${meta.frameMaterial} frame`);
  }

  if (meta.glazingType) {
    parts.push(`${meta.glazingType} glazing`);
  }

  if (meta.doorType) {
    parts.push(String(meta.doorType));
  }

  if (meta.material) {
    parts.push(String(meta.material));
  }

  if (meta.roofType) {
    parts.push(String(meta.roofType));
  }

  if (meta.pitch) {
    parts.push(`${meta.pitch} pitch`);
  }

  if (meta.complexity) {
    parts.push(String(meta.complexity));
  }

  if (meta.sourceLabel) {
    parts.push(meta.sourceLabel);
  }

  return parts.filter(Boolean).join(" · ");
}

function getBoomMergeKey(row) {
  const materialCode =
    row?.sku ||
    row?.productCode ||
    row?.meta?.sku ||
    row?.meta?.productCode ||
    "";
  const label = String(getRowDisplayLabel(row)).trim().toLowerCase();
  const unit = String(row?.unit || "").trim().toLowerCase();
  const unitPrice = round2(getRowUnitPrice(row));

  return materialCode || `${label}__${unit}__${unitPrice}`;
}

function getBoomGroups(estimate, options = {}) {
  const { merge = false } = options;
  const normalized = recalculateEstimate(normalizeEstimateForEditor(estimate || {}));
  const materialRows = normalized?.materials || [];

  if (!merge) {
    const groups = new Map();

    materialRows.forEach((row) => {
      const sourceType = getRowSourceType(row);
      const sourceId = getRowSourceId(row);
      const sourceLabel = getRowSourceLabel(row);
      const key = `${sourceType}__${sourceId}`;

      if (!groups.has(key)) {
        groups.set(key, {
          key,
          sourceType,
          sourceId,
          label:
            SOURCE_TYPE_LABELS[sourceType] || SOURCE_TYPE_LABELS.unknown,
          groupLabel: sourceLabel,
          rows: [],
        });
      }

      groups.get(key).rows.push(row);
    });

    return Array.from(groups.values())
      .map((group) => ({
        ...group,
        subtotal: round2(
          group.rows.reduce((sum, row) => sum + Number(row.total || 0), 0)
        ),
      }))
      .filter((group) => group.rows.length > 0)
      .sort((a, b) => b.subtotal - a.subtotal);
  }

  const mergedRowsMap = new Map();

  materialRows.forEach((row) => {
    const mergeKey = getBoomMergeKey(row);

    if (!mergedRowsMap.has(mergeKey)) {
      mergedRowsMap.set(mergeKey, {
        ...row,
        quantity: 0,
        qty: 0,
        total: 0,
        meta: {
          ...(row?.meta || {}),
          sourceType: "merged",
          sourceId: "all_sections",
          sourceLabel: "All Sections",
          merged: true,
          sources: [],
        },
      });
    }

    const target = mergedRowsMap.get(mergeKey);
    const nextQty = round2(getRowQuantity(target) + getRowQuantity(row));
    target.quantity = nextQty;
    target.qty = nextQty;
    target.total = round2(Number(target.total || 0) + Number(row.total || 0));

    target.meta.sources.push({
      sourceType: getRowSourceType(row),
      sourceId: getRowSourceId(row),
      sourceLabel: getRowSourceLabel(row),
    });
  });

  const mergedRows = Array.from(mergedRowsMap.values()).map((row) => ({
    ...row,
    meta: {
      ...(row.meta || {}),
      sources: Array.from(
        new Map(
          (row?.meta?.sources || []).map((source) => [
            `${source.sourceType}__${source.sourceId}`,
            source,
          ])
        ).values()
      ),
    },
  }));

  return [
    {
      key: "merged_all_sections",
      sourceType: "merged",
      sourceId: "all_sections",
      label: "Merged",
      groupLabel: "All Sections",
      rows: mergedRows,
      subtotal: round2(
        mergedRows.reduce((sum, row) => sum + Number(row.total || 0), 0)
      ),
    },
  ];
}

function buildBoomCsv(groups = [], currency = "DKK", options = {}) {
  const { merge = false } = options;

  const escapeCsv = (value) => {
    const text = String(value ?? "");
    if (text.includes(",") || text.includes('"') || text.includes("\n")) {
      return `"${text.replace(/"/g, '""')}"`;
    }
    return text;
  };

  const rows = [
    [
      "Scope",
      "Section",
      "Material",
      merge ? "Sources" : "Specification",
      "Quantity",
      "Unit",
      `Unit Price (${currency})`,
      `Total (${currency})`,
    ],
  ];

  groups.forEach((group) => {
    group.rows.forEach((row) => {
      const sources = merge
        ? (row?.meta?.sources || [])
            .map((source) => `${source.sourceType}: ${source.sourceLabel}`)
            .join(" | ")
        : formatBoomMeta(row);

      rows.push([
        group.label,
        group.groupLabel || "—",
        getRowDisplayLabel(row),
        sources,
        getRowQuantity(row),
        row.unit || "",
        getRowUnitPrice(row),
        Number(row.total || 0),
      ]);
    });
  });

  return rows.map((row) => row.map(escapeCsv).join(",")).join("\n");
}

function downloadBoomCsv(groups, currency, quoteId, options = {}) {
  const csv = buildBoomCsv(groups, currency, options);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.setAttribute("download", `boom-list-quote-${quoteId || "export"}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function SummaryMiniCard({ label, value, strong = false }) {
  return (
    <div
      className={`rounded-lg border px-3 py-2.5 ${
        strong
          ? "border-blue-800/40 bg-blue-950/10"
          : "border-gray-800 bg-gray-950/20"
      }`}
    >
      <div
        className={`text-[11px] uppercase tracking-wide ${
          strong ? "text-blue-300/70" : "text-gray-500"
        }`}
      >
        {label}
      </div>
      <div
        className={`mt-1 ${
          strong
            ? "text-lg font-semibold text-white"
            : "text-sm font-medium text-gray-100"
        }`}
      >
        {value}
      </div>
    </div>
  );
}

function StatusPill({ children, tone = "default" }) {
  const classes = {
    default: "border-gray-700 bg-gray-800/80 text-gray-300",
    warning: "border-gray-700 bg-gray-800/80 text-amber-400",
    success: "border-gray-700 bg-gray-800/80 text-emerald-400",
    info: "border-gray-700 bg-gray-800/80 text-blue-400",
    danger: "border-gray-700 bg-gray-800/80 text-red-400",
    manual: "border-gray-700 bg-gray-800/80 text-violet-400",
  };

  return (
    <span
      className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[11px] font-medium ${
        classes[tone] || classes.default
      }`}
    >
      {children}
    </span>
  );
}

function BoomListModal({
  open,
  onClose,
  groups,
  currency,
  quoteId,
  mergeBoomRows,
  onToggleMerge,
}) {
  const [expandedGroups, setExpandedGroups] = useState({});

  useEffect(() => {
    if (!open) return;
    const next = {};
    groups.forEach((group) => {
      next[group.key] = true;
    });
    setExpandedGroups(next);
  }, [open, groups]);

  if (!open) return null;

  const totalRows = groups.reduce((sum, group) => sum + group.rows.length, 0);
  const totalValue = round2(
    groups.reduce((sum, group) => sum + Number(group.subtotal || 0), 0)
  );

  const toggleGroup = (groupKey) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }));
  };

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
      <div className="flex max-h-[90vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl border border-gray-800 bg-gray-950 shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-gray-800 px-5 py-4">
          <div>
            <div className="flex items-center gap-2">
              <Package2 size={18} className="text-blue-400" />
              <h2 className="text-lg font-semibold text-white">BOOM List</h2>
            </div>
            <p className="mt-1 text-sm text-gray-400">
              Bill of materials from the current quote.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() =>
                downloadBoomCsv(groups, currency, quoteId, {
                  merge: mergeBoomRows,
                })
              }
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              <Download size={16} />
              Export CSV
            </button>

            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-gray-700 bg-gray-900 text-gray-300 hover:bg-gray-800"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="grid gap-3 border-b border-gray-800 bg-gray-900/60 px-5 py-4 md:grid-cols-4">
          <SummaryMiniCard label="Groups" value={groups.length} />
          <SummaryMiniCard label="Material rows" value={totalRows} />
          <SummaryMiniCard
            label="Mode"
            value={mergeBoomRows ? "Merged" : "By section"}
          />
          <SummaryMiniCard
            label="Material total"
            value={formatCurrency(totalValue, currency)}
            strong
          />
        </div>

        <div className="border-b border-gray-800 px-5 py-4">
          <div className="flex items-center justify-between gap-4 rounded-xl border border-gray-800 bg-gray-900 px-4 py-3">
            <div>
              <div className="text-sm font-semibold text-white">BOOM Merge</div>
              <div className="mt-1 text-xs text-gray-400">
                Merge identical materials across sections
              </div>
            </div>

            <button
              type="button"
              onClick={onToggleMerge}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
                mergeBoomRows ? "bg-blue-600" : "bg-gray-700"
              }`}
              aria-label="Toggle BOOM merge"
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${
                  mergeBoomRows ? "translate-x-5" : "translate-x-1"
                }`}
              />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5">
          {groups.length === 0 ? (
            <div className="rounded-xl border border-gray-800 bg-gray-900 p-6 text-sm text-gray-400">
              No material rows available yet. Rebuild the quote first.
            </div>
          ) : (
            <div className="space-y-4">
              {groups.map((group) => {
                const isOpen = expandedGroups[group.key];

                return (
                  <div
                    key={group.key}
                    className="overflow-hidden rounded-xl border border-gray-800 bg-gray-900"
                  >
                    <button
                      type="button"
                      onClick={() => toggleGroup(group.key)}
                      className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-white">
                          {group.groupLabel}
                        </div>
                        <div className="mt-0.5 text-xs text-gray-400">
                          {group.label} · {group.rows.length} material row
                          {group.rows.length === 1 ? "" : "s"} ·{" "}
                          {formatCurrency(group.subtotal, currency)}
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <StatusPill tone="info">{group.rows.length} rows</StatusPill>
                        <div className="rounded-lg border border-gray-700 bg-gray-800 p-2 text-gray-300">
                          {isOpen ? (
                            <ChevronDown size={16} />
                          ) : (
                            <ChevronRight size={16} />
                          )}
                        </div>
                      </div>
                    </button>

                    {isOpen && (
                      <div className="border-t border-gray-800">
                        <div className="hidden grid-cols-[2fr_2fr_0.8fr_0.8fr_1fr_1fr] gap-3 border-b border-gray-800 bg-gray-950/60 px-4 py-2 text-[11px] uppercase tracking-wide text-gray-500 md:grid">
                          <div>Material</div>
                          <div>{mergeBoomRows ? "Sources" : "Specification"}</div>
                          <div>Qty</div>
                          <div>Unit</div>
                          <div>Unit price</div>
                          <div>Total</div>
                        </div>

                        <div className="divide-y divide-gray-800">
                          {group.rows.map((row, index) => {
                            const sources = mergeBoomRows
                              ? (row?.meta?.sources || [])
                                  .map(
                                    (source) =>
                                      `${source.sourceType}: ${source.sourceLabel}`
                                  )
                                  .join(" | ")
                              : formatBoomMeta(row);

                            return (
                              <div
                                key={`${group.key}-${index}-${getRowDisplayLabel(row)}`}
                                className="grid gap-2 px-4 py-3 md:grid-cols-[2fr_2fr_0.8fr_0.8fr_1fr_1fr] md:items-center"
                              >
                                <div>
                                  <div className="text-sm font-medium text-white">
                                    {getRowDisplayLabel(row)}
                                  </div>
                                  <div className="mt-1 text-xs text-gray-400 md:hidden">
                                    {sources || "—"}
                                  </div>
                                </div>

                                <div className="hidden text-sm text-gray-300 md:block">
                                  {sources || "—"}
                                </div>

                                <div className="text-sm text-white">
                                  {getRowQuantity(row)}
                                </div>

                                <div className="text-sm text-gray-300">
                                  {row.unit || "—"}
                                </div>

                                <div className="text-sm text-gray-300">
                                  {formatCurrency(getRowUnitPrice(row), currency)}
                                </div>

                                <div className="text-sm font-semibold text-white">
                                  {formatCurrency(row.total || 0, currency)}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function QuoteDetails({ quoteId: propQuoteId }) {
  const params = useParams();
  const id = propQuoteId || params.id;
  const { workspace } = useAuth();
  const editorsRef = useRef(null);
  const lastHistoryPushRef = useRef(0);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rebuilding, setRebuilding] = useState(false);
  const [regeneratingText, setRegeneratingText] = useState(false);

  const [requestText, setRequestText] = useState("");
  const [parsedData, setParsedData] = useState({});
  const [estimate, setEstimate] = useState(null);
  const [quoteText, setQuoteText] = useState("");
  const [customer, setCustomer] = useState(null);
  const [imageUrls, setImageUrls] = useState([]);
  const [status, setStatus] = useState("draft");
  const [updatedAt, setUpdatedAt] = useState(null);
  const [versions, setVersions] = useState([]);
  const [errors, setErrors] = useState({});
  const [saveMessage, setSaveMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showBoomList, setShowBoomList] = useState(false);
  const [mergeBoomRows, setMergeBoomRows] = useState(false);
  const [history, setHistory] = useState([]);

  const [windows, setWindows] = useState([]);
  const [doors, setDoors] = useState([]);
  const [roofs, setRoofs] = useState([]);

  const isBusy = saving || rebuilding || regeneratingText;

  const selectedCurrency = useMemo(() => {
    if (parsedData?.currency === "SEK" || parsedData?.currency === "DKK") {
      return parsedData.currency;
    }
    return parsedData?.country === "SE" ? "SEK" : "DKK";
  }, [parsedData]);

  const normalizedEstimate = useMemo(
    () => recalculateEstimate(normalizeEstimateForEditor(estimate || {})),
    [estimate]
  );

  const estimateSectionGroups = useMemo(
    () => groupEstimateBySection(normalizedEstimate || {}),
    [normalizedEstimate]
  );

  const boomGroups = useMemo(
    () => getBoomGroups(normalizedEstimate || {}, { merge: mergeBoomRows }),
    [normalizedEstimate, mergeBoomRows]
  );

  const reviewFlags = useMemo(
    () => safeArray(normalizedEstimate?.meta?.reviewFlags),
    [normalizedEstimate]
  );

  const pushHistorySnapshot = useCallback(
    (snapshotEstimate, label = "Edit") => {
      if (!snapshotEstimate) return;

      const now = Date.now();

      if (label === "Row edit" && now - lastHistoryPushRef.current < 750) {
        return;
      }

      lastHistoryPushRef.current = now;

      setHistory((prev) => [
        ...prev.slice(-4),
        {
          label,
          createdAt: new Date().toISOString(),
          requestText,
          parsedData,
          estimate: snapshotEstimate,
          quoteText,
          imageUrls,
          status,
          windows,
          doors,
          roofs,
        },
      ]);
    },
    [requestText, parsedData, quoteText, imageUrls, status, windows, doors, roofs]
  );

  const handleUndo = useCallback(() => {
    setHistory((prev) => {
      const last = prev[prev.length - 1];
      if (!last) return prev;

      setRequestText(last.requestText || "");
      setParsedData(safeObject(last.parsedData, {}));
      setEstimate(recalculateEstimate(normalizeEstimateForEditor(last.estimate || {})));
      setQuoteText(last.quoteText || "");
      setImageUrls(safeArray(last.imageUrls));
      setStatus(last.status || "draft");
      setWindows(Array.isArray(last.windows) ? last.windows : []);
      setDoors(Array.isArray(last.doors) ? last.doors : []);
      setRoofs(Array.isArray(last.roofs) ? last.roofs : []);
      setHasUnsavedChanges(true);
      setSaveMessage(`Undid: ${last.label || "last change"}`);
      setErrorMessage("");

      return prev.slice(0, -1);
    });
  }, []);

  useEffect(() => {
    setParsedData((prev) => {
      const prevWindows = Array.isArray(prev?.windows) ? prev.windows : [];
      const prevDoors = Array.isArray(prev?.doors) ? prev.doors : [];
      const prevRoofs = Array.isArray(prev?.roofs) ? prev.roofs : [];

      const windowsChanged =
        JSON.stringify(prevWindows) !== JSON.stringify(windows);
      const doorsChanged = JSON.stringify(prevDoors) !== JSON.stringify(doors);
      const roofsChanged = JSON.stringify(prevRoofs) !== JSON.stringify(roofs);

      if (!windowsChanged && !doorsChanged && !roofsChanged) {
        return prev;
      }

      return {
        ...prev,
        windows,
        doors,
        roofs,
      };
    });
  }, [windows, doors, roofs]);

  const loadVersions = useCallback(async () => {
    if (!id) {
      setVersions([]);
      return;
    }

    try {
      const { data, error } = await supabase
        .from("quote_versions")
        .select("*")
        .eq("quote_id", id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setVersions(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("loadVersions failed:", error);
      setVersions([]);
    }
  }, [id]);

  const loadQuote = useCallback(async () => {
    if (!id) {
      setErrorMessage("Missing quote ID");
      setEstimate(null);
      setVersions([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setErrorMessage("");
      setSaveMessage("");

      const { data, error } = await supabase
        .from("quotes")
        .select("*")
        .eq("id", id)
        .single();

      if (error) {
        throw new Error(error.message || "Failed to load quote");
      }

      if (!data) {
        throw new Error("Quote not found");
      }

      const parsed = safeObject(data.parsed_json, {});
      if (!parsed.projectName?.trim()) {
  const customerName =
    parsed?.customer?.name ||
    data?.customer_snapshot?.name ||
    "Customer";

  const projectType =
    parsed?.primaryProjectType ||
    parsed?.projectType ||
    "project";

  parsed.projectName = `${customerName} - ${projectType}`;
}
      const nextEstimate = recalculateEstimate(
        normalizeEstimateForEditor(data.estimate_json)
      );

      const nextImageUrls = safeArray(data.image_urls);
      const nextWindows = Array.isArray(parsed.windows) ? parsed.windows : [];
      const nextDoors = Array.isArray(parsed.doors) ? parsed.doors : [];
      const nextRoofs = Array.isArray(parsed.roofs) ? parsed.roofs : [];

      setRequestText(data.request_text || "");
      setParsedData(parsed);
      setEstimate(nextEstimate);
      setQuoteText(data.quote_text || "");
      setImageUrls(nextImageUrls);
      setStatus(data.status || "draft");
      setUpdatedAt(data.updated_at || null);
      setWindows(nextWindows);
      setDoors(nextDoors);
      setRoofs(nextRoofs);
      setErrors(
        validateParsedData({
          ...parsed,
          windows: nextWindows,
          doors: nextDoors,
          roofs: nextRoofs,
        })
      );
      setHasUnsavedChanges(false);
      setHistory([]);

      await loadVersions();
    } catch (error) {
      console.error("loadQuote failed:", error);
      setErrorMessage(error.message || "Failed to load quote");
      setEstimate(null);
      setVersions([]);
    } finally {
      setLoading(false);
    }
  }, [id, loadVersions]);

  useEffect(() => {
    loadQuote();
  }, [loadQuote]);

  useEffect(() => {
    setErrors(
      validateParsedData({
        ...parsedData,
        windows,
        doors,
        roofs,
      })
    );
  }, [parsedData, windows, doors, roofs]);

  const handleCustomerChange = (nextCustomer) => {
  setCustomer(nextCustomer);
  setHasUnsavedChanges(true);
  setSaveMessage("");
  setErrorMessage("");
};

  const handleRebuild = async () => {
    const rebuildPayload = {
      ...parsedData,
      windows,
      doors,
      roofs,
      marginPercent: Number(
        normalizedEstimate?.meta?.marginPercent ?? parsedData?.marginPercent ?? 25
      ),
    };

    const validationErrors = validateParsedData(rebuildPayload);
    setErrors(validationErrors);
    setSaveMessage("");
    setErrorMessage("");

    if (Object.keys(validationErrors).length > 0) {
      setErrorMessage("Fix validation errors before rebuilding.");
      return;
    }

    try {
      pushHistorySnapshot(normalizedEstimate, "Rebuild estimate");
      setRebuilding(true);

      const oldMaterialsCount = (normalizedEstimate?.materials || []).length;
      const oldLabourCount = (normalizedEstimate?.labour || []).length;
      const oldExtrasCount = (normalizedEstimate?.extras || []).length;
      const oldTotal = Number(normalizedEstimate?.summary?.total || 0);

      const rebuilt = await calculateQuoteFromBackend(rebuildPayload, requestText);
      const merged = mergeEstimatePreserveManual(normalizedEstimate, rebuilt);

      setParsedData(rebuildPayload);
      setEstimate(merged);
      setHasUnsavedChanges(true);

      const newMaterialsCount = (merged?.materials || []).length;
      const newLabourCount = (merged?.labour || []).length;
      const newExtrasCount = (merged?.extras || []).length;
      const newTotal = Number(merged?.summary?.total || 0);

      const materialDiff = newMaterialsCount - oldMaterialsCount;
      const labourDiff = newLabourCount - oldLabourCount;
      const extrasDiff = newExtrasCount - oldExtrasCount;
      const totalDiff = round2(newTotal - oldTotal);

      setSaveMessage(
        `Quote rebuilt. Materials ${materialDiff >= 0 ? "+" : ""}${materialDiff}, labour ${labourDiff >= 0 ? "+" : ""}${labourDiff}, extras ${extrasDiff >= 0 ? "+" : ""}${extrasDiff}, total ${totalDiff >= 0 ? "+" : ""}${formatCurrency(totalDiff, selectedCurrency)}.`
      );
    } catch (error) {
      console.error("handleRebuild failed:", error);
      setErrorMessage(error.message || "Failed to rebuild quote");
    } finally {
      setRebuilding(false);
    }
  };

  const handleSave = async () => {
  const currentMarginPercent = Number(
  normalizedEstimate?.summary?.marginPercent ??
    normalizedEstimate?.meta?.marginPercent ??
    parsedData?.marginPercent ??
    25
);

  const finalParsed = {
    ...parsedData,
    windows,
    doors,
    roofs,
    marginPercent: currentMarginPercent,
  };

  const validationErrors = validateParsedData(finalParsed);
  setErrors(validationErrors);
  setSaveMessage("");
  setErrorMessage("");

  if (Object.keys(validationErrors).length > 0) {
    setErrorMessage("Fix validation errors before saving.");
    return;
  }

  try {
    setSaving(true);

    const finalEstimate = recalculateEstimate({
      ...normalizeEstimateForEditor(normalizedEstimate || {}),
      meta: {
        ...(normalizedEstimate?.meta || {}),
        marginPercent: currentMarginPercent,
      },
    });

    const saved = await updateQuote(id, {
      requestText,

      parsedQuote: {
        ...finalParsed,
        customer,
      },

      estimate: finalEstimate,
      quoteText,
      imageUrls,
      status,

      workspace_id: workspace?.id,

      customer_id: customer?.id || null,
    });

    setParsedData(finalParsed);
    setEstimate(finalEstimate);
    setUpdatedAt(saved?.updated_at || new Date().toISOString());
    setHasUnsavedChanges(false);
    setHistory([]);
    setSaveMessage("Quote saved.");
    await loadVersions();
  } catch (error) {
    console.error("handleSave failed:", error);
    setErrorMessage(error.message || "Failed to save quote");
  } finally {
    setSaving(false);
  }
};

  const handleRegenerateText = async () => {
    try {
      pushHistorySnapshot(normalizedEstimate, "Regenerate quote text");
      setRegeneratingText(true);
      setSaveMessage("");
      setErrorMessage("");

      const result = await regenerateQuoteText(
        {
          ...parsedData,
          windows,
          doors,
          roofs,
        },
        normalizedEstimate,
        {
          tone: "premium",
        }
      );

      setQuoteText(result?.quoteText || "");
      setHasUnsavedChanges(true);
      setSaveMessage("Quote text regenerated.");
    } catch (error) {
      console.error("handleRegenerateText failed:", error);
      setErrorMessage(error.message || "Failed to regenerate quote text");
    } finally {
      setRegeneratingText(false);
    }
  };

  const handleUpdateTimeRows = (nextTimeRows = []) => {
  setEstimate((prev) => {
    const current = recalculateEstimate(normalizeEstimateForEditor(prev || {}));

    pushHistorySnapshot(current, "Time edit");

    return {
      ...current,
      timeRows: nextTimeRows,
      meta: {
        ...(current.meta || {}),
        timeRowsUpdatedAt: new Date().toISOString(),
      },
    };
  });

  setHasUnsavedChanges(true);
};

  const handleChangeMarginPercent = useCallback((nextMarginPercent) => {
  const safeMargin = Number(nextMarginPercent);

  if (!Number.isFinite(safeMargin)) return;

  setEstimate((prev) => {
    const current = normalizeEstimateForEditor(prev || {});

    return recalculateEstimate({
      ...current,
      meta: {
        ...(current.meta || {}),
        marginPercent: safeMargin,
      },
      summary: {
        ...(current.summary || {}),
        marginPercent: safeMargin,
      },
    });
  });

  setParsedData((prev) => ({
    ...(prev || {}),
    marginPercent: safeMargin,
  }));

  setHasUnsavedChanges(true);
  setSaveMessage("");
  setErrorMessage("");
}, []);

  const handleUpdateEstimateRow = (rowId, rowKind, updates = {}) => {
    setEstimate((prev) => {
      const current = recalculateEstimate(normalizeEstimateForEditor(prev || {}));
      pushHistorySnapshot(current, "Row edit");

      const updateRows = (rows = [], kind) =>
        rows.map((row) => {
          if (row.id !== rowId) return row;

          const quantity = Number(
            updates.quantity ??
              updates.qty ??
              updates.hours ??
              row.quantity ??
              row.qty ??
              row.hours ??
              0
          );

          const unitPrice = Number(
            updates.unitPrice ??
              updates.price ??
              updates.rate ??
              updates.hourlyRate ??
              row.unitPrice ??
              row.price ??
              row.rate ??
              row.hourlyRate ??
              0
          );

          return {
            ...row,
            ...updates,
            quantity,
            qty: kind === "material" || kind === "extra" ? quantity : row.qty,
            hours: kind === "labour" ? quantity : row.hours,
            unitPrice,
            price:
              kind === "material" || kind === "extra" ? unitPrice : row.price,
            hourlyRate: kind === "labour" ? unitPrice : row.hourlyRate,
            rate: kind === "labour" ? unitPrice : row.rate,
            total: round2(quantity * unitPrice),
            manuallyEdited: true,
            flags: {
              ...(row.flags || {}),
              manuallyEdited: true,
              reviewRequired: false,
            },
            meta: {
              ...(row.meta || {}),
              ...(updates.meta || {}),
              notes:
                updates?.meta?.notes !== undefined
                  ? updates.meta.notes
                  : row.meta?.notes || "",
              review: false,
            },
          };
        });

      return recalculateEstimate({
        ...current,
        materials: updateRows(current.materials || [], "material"),
        labour: updateRows(current.labour || [], "labour"),
        extras: updateRows(current.extras || [], "extra"),
      });
    });

    setHasUnsavedChanges(true);
  };

  const handleRestoreVersion = (version) => {
    pushHistorySnapshot(normalizedEstimate, "Restore version");

    const parsed = safeObject(version?.parsed_json, {});
    const restoredEstimate = recalculateEstimate(
      normalizeEstimateForEditor(version?.estimate_json || {})
    );

    const nextWindows = Array.isArray(parsed.windows) ? parsed.windows : [];
    const nextDoors = Array.isArray(parsed.doors) ? parsed.doors : [];
    const nextRoofs = Array.isArray(parsed.roofs) ? parsed.roofs : [];

    setRequestText(version?.request_text || "");
    setParsedData(parsed);
    setEstimate(restoredEstimate);
    setQuoteText(version?.quote_text || "");
    setImageUrls(safeArray(version?.image_urls));
    setStatus(version?.status || "draft");
    setWindows(nextWindows);
    setDoors(nextDoors);
    setRoofs(nextRoofs);
    setErrors(
      validateParsedData({
        ...parsed,
        windows: nextWindows,
        doors: nextDoors,
        roofs: nextRoofs,
      })
    );
    setHasUnsavedChanges(true);
    setSaveMessage("Version restored into editor. Save to make it current.");
    setErrorMessage("");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 px-6 py-6 text-gray-200">
        <div className="mx-auto max-w-7xl space-y-2 rounded-xl border border-yellow-700 bg-gray-900 p-6">
          <div className="font-semibold text-yellow-300">Loading quote...</div>
          <div className="text-sm text-gray-400">Quote ID: {String(id)}</div>
        </div>
      </div>
    );
  }

  if (!estimate) {
    return (
      <div className="min-h-screen bg-gray-950 px-6 py-6 text-gray-200">
        <div className="mx-auto max-w-7xl rounded-xl border border-red-700 bg-gray-900 p-6">
          <div className="flex items-start gap-2 text-red-300">
            <span>{errorMessage || "Quote could not be loaded."}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <QuoteDetailsPage
  quote={{
    id,
    customer,
    customer_name: getCustomerName(customer),
    customer_id:
      customer?.id ||
      parsedData?.customerId ||
      parsedData?.customer_id ||
      null,

    estimate_json: normalizedEstimate,

    parsed_json: {
      ...parsedData,
      windows,
      doors,
      roofs,
      customer,
      customerId:
        customer?.id ||
        parsedData?.customerId ||
        parsedData?.customer_id ||
        null,
      customer_id:
        customer?.id ||
        parsedData?.customerId ||
        parsedData?.customer_id ||
        null,
    },

    quote_text: quoteText,
    image_urls: imageUrls,
    request_text: requestText,
    status,
    updated_at: updatedAt,
    save_message: saveMessage,
    error_message: errorMessage,
    has_unsaved_changes: hasUnsavedChanges,
    validation_errors: errors,
    can_undo: history.length > 0,
    last_saved_at: updatedAt,
  }}

  setCustomer={setCustomer}
  onCustomerChange={handleCustomerChange}

  estimate={normalizedEstimate}

  parsedData={{
    ...parsedData,
    windows,
    doors,
    roofs,
    customer,
    customerId:
      customer?.id ||
      parsedData?.customerId ||
      parsedData?.customer_id ||
      null,
    customer_id:
      customer?.id ||
      parsedData?.customerId ||
      parsedData?.customer_id ||
      null,
  }}

  setParsedData={setParsedData}

  errors={errors}

  windows={windows}
  setWindows={setWindows}

  doors={doors}
  setDoors={setDoors}

  roofs={roofs}
  setRoofs={setRoofs}

  isBusy={isBusy}

  onRebuild={handleRebuild}
  onOpenBoom={() => setShowBoomList(true)}
  onSave={handleSave}
  onRegenerateQuoteText={handleRegenerateText}

  onUpdateEstimateRow={handleUpdateEstimateRow}
  onUpdateTimeRows={handleUpdateTimeRows}
  onChangeMarginPercent={handleChangeMarginPercent}

  onUndo={handleUndo}
  canUndo={history.length > 0}

  lastSavedAt={updatedAt}

  versions={versions}
  onRestoreVersion={handleRestoreVersion}

  editorsRef={editorsRef}
  reviewFlags={reviewFlags}
  sectionGroups={estimateSectionGroups}

  selectedCurrency={selectedCurrency}
  formatDateTime={formatDateTime}
/>

      <BoomListModal
        open={showBoomList}
        onClose={() => setShowBoomList(false)}
        groups={boomGroups}
        currency={selectedCurrency}
        quoteId={id}
        mergeBoomRows={mergeBoomRows}
        onToggleMerge={() => setMergeBoomRows((prev) => !prev)}
      />
    </>
  );
}
